import React, { useState, useRef, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  Flex,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  VStack,
  List,
  ListItem,
  ListIcon,
  Alert,
  AlertIcon,
  CloseButton,
} from '@chakra-ui/react'

export default function AddAccountsModal({ onClose, onAdd, currentCount, maxCount }) {
  const [chips, setChips] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const sanitizeHandle = (handle) => {
    // Remove any @ prefix first, then prepend single @ for accounts
    const cleaned = handle.trim().replace(/^@+/, '')
    if (!cleaned) return null
    return `@${cleaned}`  // Always add @ prefix for accounts
  }

  const addChip = (handle) => {
    const cleaned = sanitizeHandle(handle)
    if (!cleaned) return

    // Check if already exists (case-insensitive)
    if (chips.some(c => c.toLowerCase() === cleaned.toLowerCase())) {
      setError(`Duplicate handle: ${cleaned}`)
      return
    }

    const totalAfterAdd = currentCount + chips.length + 1
    if (totalAfterAdd > maxCount) {
      setError(`Cannot exceed ${maxCount} accounts`)
      return
    }

    setChips(prev => [...prev, cleaned])
    setInputValue('')
    setError(null)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault()
      if (inputValue.trim()) {
        addChip(inputValue)
      }
    } else if (e.key === 'Backspace' && !inputValue && chips.length > 0) {
      // Delete last chip on backspace when input is empty
      setChips(prev => prev.slice(0, -1))
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    // Split by newline, comma, space, tab
    const handles = pasted.split(/[\n,\s\t]+/).filter(Boolean)

    const newChips = []
    for (const h of handles) {
      const cleaned = sanitizeHandle(h)
      if (!cleaned) continue
      if (chips.some(c => c.toLowerCase() === cleaned.toLowerCase())) continue
      if (newChips.some(c => c.toLowerCase() === cleaned.toLowerCase())) continue

      const totalAfter = currentCount + chips.length + newChips.length + 1
      if (totalAfter > maxCount) {
        setError(`Cannot exceed ${maxCount} accounts. Some handles were not added.`)
        break
      }
      newChips.push(cleaned)
    }

    setChips(prev => [...prev, ...newChips])
    setInputValue('')
  }

  const removeChip = (index) => {
    setChips(prev => prev.filter((_, i) => i !== index))
    setError(null)
  }

  const handleSubmit = async () => {
    if (chips.length === 0) return

    setSubmitting(true)
    setError(null)
    try {
      await onAdd(chips)
      // Success - modal closed by parent
    } catch (e) {
      setError(e.message)
      setSubmitting(false)
    }
  }

  const totalCount = currentCount + chips.length

  return (
    <Modal isOpen={true} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="#131316" border="1px solid" borderColor="#27272a" mx={4}>
        <ModalHeader color="#fafafa" display="flex" justifyContent="space-between" alignItems="center">
          <Text>Add Accounts</Text>
          <CloseButton onClick={onClose} color="gray.400" _hover={{ color: 'red.400' }} />
        </ModalHeader>
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text 
              fontSize="lg" 
              fontWeight="500"
              color={totalCount >= maxCount ? 'red.400' : 'brand.500'}
              textAlign="center"
            >
              {totalCount} / {maxCount} accounts
            </Text>

            <Box
              p={3}
              bg="#09090b"
              border="1px solid"
              borderColor="#27272a"
              borderRadius="10px"
              minH="120px"
              maxH="400px"
              overflowY="auto"
            >
              <Flex flexWrap="wrap" gap={2}>
                {chips.map((chip, idx) => (
                  <Tag
                    key={idx}
                    size="md"
                    bgGradient="linear(to-br, #c2410c, #ea580c)"
                    color="#fafafa"
                    border="1px solid"
                    borderColor="#9a3412"
                    borderRadius="full"
                  >
                    <TagLabel>{chip}</TagLabel>
                    <TagCloseButton onClick={() => removeChip(idx)} color="red.400" />
                  </Tag>
                ))}
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder="Type username and press Enter, Space, or Comma"
                  variant="unstyled"
                  flex="1"
                  minW="200px"
                  color="#e4e4e7"
                  fontSize="md"
                  isDisabled={totalCount >= maxCount}
                  _placeholder={{ color: '#52525b' }}
                />
              </Flex>
            </Box>

            {error && (
              <Alert status="error" bg="rgba(255, 107, 107, 0.1)" border="1px solid" borderColor="rgba(255, 107, 107, 0.3)" borderRadius="8px">
                <AlertIcon color="red.400" />
                <Text color="red.400" fontSize="sm">{error}</Text>
              </Alert>
            )}

            <Box
              p={3}
              bg="rgba(95, 209, 199, 0.05)"
              border="1px solid"
              borderColor="rgba(95, 209, 199, 0.2)"
              borderRadius="8px"
            >
              <List spacing={1} fontSize="sm" color="gray.400">
                <ListItem>
                  <Text as="span" color="brand.500" mr={2}>â€¢</Text>
                  Type a username and press Enter, Space, or Comma to add
                </ListItem>
                <ListItem>
                  <Text as="span" color="brand.500" mr={2}>â€¢</Text>
                  Paste multiple usernames (separated by newlines, commas, or spaces)
                </ListItem>
                <ListItem>
                  <Text as="span" color="brand.500" mr={2}>â€¢</Text>
                  Press Backspace to remove the last chip
                </ListItem>
                <ListItem>
                  <Text as="span" color="brand.500" mr={2}>â€¢</Text>
                  Type a Twitter username (@ is optional and will be auto-added)
                </ListItem>
              </List>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button
            bgGradient="linear(to-br, #c2410c, #ea580c)"
            color="#fafafa"
            border="1px solid"
            borderColor="#9a3412"
            _hover={{
              borderColor: '#fb923c',
              shadow: 'lg',
            }}
            onClick={handleSubmit}
            isDisabled={chips.length === 0 || submitting}
            isLoading={submitting}
            loadingText="Adding..."
          >
            Add {chips.length} Account{chips.length !== 1 ? 's' : ''}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
